import { useMemo, useState } from 'react';
import type { Transcript } from './SearchTranscript.types';

interface FullKBReviewFinalResultProps {
  result: Record<string, unknown>;
}

function FullKBReviewFinalResult({ result }: FullKBReviewFinalResultProps) {
  const dist = (result['relevance_distribution'] ?? {}) as Record<string, number>;
  const details = (result['document_details'] ?? []) as Array<Record<string, unknown>>;
  const findings = (result['key_findings'] ?? []) as string[];
  const gaps = (result['gaps_identified'] ?? []) as string[];
  const summary = (result['state_of_the_art_summary'] ?? '') as string;

  const formatCents = (cents: unknown) => {
    if (typeof cents !== 'number') return '—';
    return `$${(cents / 100).toFixed(2)}`;
  };

  return (
    <div className="final-result" style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      <section>
        <h3 style={{ marginBottom: '0.5rem' }}>Full KB Review — Results</h3>
        <table style={{ fontSize: '0.85rem', borderCollapse: 'collapse', width: '100%' }}>
          <tbody>
            {[
              ['Documents reviewed', result['total_documents']],
              ['Skipped (no text)', result['skipped_documents']],
              ['Relevant (above threshold)', result['relevant_documents']],
              ['Relevance threshold', `${result['relevance_threshold']}%`],
              ['Documents enriched', result['documents_enriched']],
              ['Total tokens used', typeof result['total_tokens_used'] === 'number' ? (result['total_tokens_used'] as number).toLocaleString() : '—'],
              ['Estimated cost', formatCents(result['estimated_cost_cents'])],
            ].map(([label, value]) => (
              <tr key={String(label)}>
                <td style={{ padding: '2px 12px 2px 0', color: '#6b7280', whiteSpace: 'nowrap' }}>{String(label)}</td>
                <td style={{ padding: '2px 0', fontWeight: 500 }}>{String(value ?? '—')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {Object.keys(dist).length > 0 && (
        <section>
          <h4 style={{ marginBottom: '0.4rem', fontSize: '0.9rem' }}>Relevance Distribution</h4>
          <table style={{ fontSize: '0.85rem', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {Object.keys(dist).map(k => (
                  <th key={k} style={{ padding: '2px 12px 2px 0', color: '#6b7280', fontWeight: 'normal', textAlign: 'left' }}>{k}%</th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                {Object.values(dist).map((v, i) => (
                  <td key={i} style={{ padding: '2px 12px 2px 0', fontWeight: 500 }}>{v}</td>
                ))}
              </tr>
            </tbody>
          </table>
        </section>
      )}

      {summary && (
        <section>
          <h4 style={{ marginBottom: '0.4rem', fontSize: '0.9rem' }}>Executive Summary</h4>
          <div style={{ fontSize: '0.85rem', lineHeight: 1.6, whiteSpace: 'pre-wrap', background: '#f8fafc', padding: '0.75rem', borderRadius: 4 }}>
            {summary}
          </div>
        </section>
      )}

      {findings.length > 0 && (
        <section>
          <h4 style={{ marginBottom: '0.4rem', fontSize: '0.9rem' }}>Key Findings</h4>
          <ul style={{ margin: 0, paddingLeft: '1.2rem', fontSize: '0.85rem', lineHeight: 1.7 }}>
            {findings.map((f, i) => <li key={i}>{f}</li>)}
          </ul>
        </section>
      )}

      {gaps.length > 0 && (
        <section>
          <h4 style={{ marginBottom: '0.4rem', fontSize: '0.9rem' }}>Gaps Identified</h4>
          <ul style={{ margin: 0, paddingLeft: '1.2rem', fontSize: '0.85rem', lineHeight: 1.7 }}>
            {gaps.map((g, i) => <li key={i}>{g}</li>)}
          </ul>
        </section>
      )}

      {details.length > 0 && (
        <section>
          <h4 style={{ marginBottom: '0.4rem', fontSize: '0.9rem' }}>Document Results ({details.length})</h4>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ fontSize: '0.78rem', borderCollapse: 'collapse', width: '100%' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                  <th style={{ padding: '4px 8px 4px 0', textAlign: 'left', color: '#6b7280', fontWeight: 'normal' }}>Document</th>
                  <th style={{ padding: '4px 8px', textAlign: 'center', color: '#6b7280', fontWeight: 'normal' }}>Relevance</th>
                  <th style={{ padding: '4px 8px', textAlign: 'center', color: '#6b7280', fontWeight: 'normal' }}>Enriched</th>
                  <th style={{ padding: '4px 0', textAlign: 'left', color: '#6b7280', fontWeight: 'normal' }}>Rationale</th>
                </tr>
              </thead>
              <tbody>
                {details.map((d, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '3px 8px 3px 0', fontWeight: 500 }}>{String(d['label'] ?? d['document_id'] ?? '')}</td>
                    <td style={{ padding: '3px 8px', textAlign: 'center' }}>
                      <span style={{
                        background: (d['relevance_percent'] as number) >= 50 ? '#d1fae5' : (d['relevance_percent'] as number) >= 30 ? '#fef9c3' : '#f3f4f6',
                        color: (d['relevance_percent'] as number) >= 50 ? '#065f46' : (d['relevance_percent'] as number) >= 30 ? '#713f12' : '#4b5563',
                        padding: '1px 6px', borderRadius: 3, fontSize: '0.75rem',
                      }}>
                        {String(d['relevance_percent'])}%
                      </span>
                    </td>
                    <td style={{ padding: '3px 8px', textAlign: 'center' }}>{d['enriched'] ? 'Yes' : '—'}</td>
                    <td style={{ padding: '3px 0', color: '#6b7280' }}>{String(d['rationale'] ?? '')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}

interface FRSSummaryViewProps {
  result: Record<string, unknown>;
  transcript: Transcript;
  kbName: string | null;
}

export function FRSSummaryView({ result, transcript, kbName }: FRSSummaryViewProps) {
  const [docSortBy, setDocSortBy] = useState<'relevance' | 'title'>('relevance');

  const totalDocs    = (result['total_documents'] as number) ?? 0;
  const skippedDocs  = (result['skipped_documents'] as number) ?? 0;
  const relevantDocs = (result['relevant_documents'] as number) ?? 0;
  const threshold    = (result['relevance_threshold'] as number) ?? 0;
  const docsEnriched = (result['documents_enriched'] as number) ?? 0;
  const tokensUsed   = (result['total_tokens_used'] as number) ?? 0;
  const costCents    = (result['estimated_cost_cents'] as number) ?? 0;
  const dist         = ((result['relevance_distribution'] ?? {}) as Record<string, number>);
  const details      = ((result['document_details'] ?? []) as Array<Record<string, unknown>>);
  const findings     = ((result['key_findings'] ?? []) as string[]);
  const gaps         = ((result['gaps_identified'] ?? []) as string[]);
  const summary      = (result['state_of_the_art_summary'] ?? '') as string;
  const kbDisplayName = kbName ?? (result['kb_id'] as string) ?? '—';

  const durationSec = transcript.completed_at && transcript.started_at
    ? Math.round((new Date(transcript.completed_at).getTime() - new Date(transcript.started_at).getTime()) / 1000)
    : null;

  const formatDuration = (sec: number) => sec < 60 ? `${sec}s` : `${Math.floor(sec / 60)}m ${sec % 60}s`;
  const formatCost = (cents: number) => `$${(cents / 100).toFixed(2)}`;

  const distBuckets = [
    { key: '0-15',   label: '0–15%',   color: '#cbd5e1', textColor: '#475569' },
    { key: '16-30',  label: '16–30%',  color: '#fde68a', textColor: '#92400e' },
    { key: '31-50',  label: '31–50%',  color: '#fdba74', textColor: '#7c2d12' },
    { key: '51-75',  label: '51–75%',  color: '#86efac', textColor: '#14532d' },
    { key: '76-100', label: '76–100%', color: '#4ade80', textColor: '#14532d' },
  ];
  const distTotal = distBuckets.reduce((s, b) => s + (dist[b.key] ?? 0), 0);

  const sortedDocs = useMemo(() => {
    const docs = [...details];
    if (docSortBy === 'relevance') {
      docs.sort((a, b) => ((b['relevance_percent'] as number) ?? 0) - ((a['relevance_percent'] as number) ?? 0));
    } else {
      docs.sort((a, b) => String(a['label'] ?? '').localeCompare(String(b['label'] ?? '')));
    }
    return docs;
  }, [details, docSortBy]);

  const relevanceBadge = (pct: number) => {
    if (pct >= 51) return { bg: '#d1fae5', text: '#065f46', bar: '#4ade80' };
    if (pct >= 31) return { bg: '#fef9c3', text: '#713f12', bar: '#fbbf24' };
    return { bg: '#f3f4f6', text: '#4b5563', bar: '#cbd5e1' };
  };

  return (
    <div className="frs-summary">
      <div className="frs-context-bar">
        <span className="frs-context-type">Full KB Review</span>
        <span className="frs-context-sep">|</span>
        <span className="frs-context-kb">{kbDisplayName}</span>
        {transcript.started_at && (
          <>
            <span className="frs-context-sep">|</span>
            <span className="frs-context-date">
              {new Date(transcript.started_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
              {' '}
              {new Date(transcript.started_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
            </span>
          </>
        )}
        {durationSec !== null && (
          <>
            <span className="frs-context-sep">|</span>
            <span className="frs-context-duration">{formatDuration(durationSec)}</span>
          </>
        )}
      </div>

      <div className="frs-stats-row">
        <div className="frs-stat-card"><div className="frs-stat-value">{totalDocs.toLocaleString()}</div><div className="frs-stat-label">Documents reviewed</div></div>
        {skippedDocs > 0 && (
          <div className="frs-stat-card frs-stat-muted"><div className="frs-stat-value">{skippedDocs.toLocaleString()}</div><div className="frs-stat-label">Skipped (no text)</div></div>
        )}
        <div className="frs-stat-card frs-stat-accent"><div className="frs-stat-value">{relevantDocs.toLocaleString()}</div><div className="frs-stat-label">Relevant (&gt;={threshold}%)</div></div>
        <div className="frs-stat-card"><div className="frs-stat-value">{docsEnriched.toLocaleString()}</div><div className="frs-stat-label">Enriched</div></div>
        {tokensUsed > 0 && (
          <div className="frs-stat-card frs-stat-muted"><div className="frs-stat-value">{tokensUsed.toLocaleString()}</div><div className="frs-stat-label">Tokens used</div></div>
        )}
        {costCents > 0 && (
          <div className="frs-stat-card frs-stat-muted"><div className="frs-stat-value">{formatCost(costCents)}</div><div className="frs-stat-label">Estimated cost</div></div>
        )}
      </div>

      {distTotal > 0 && (
        <div className="frs-section">
          <div className="frs-section-title">Relevance Distribution</div>
          <div className="frs-dist-bar">
            {distBuckets.map(b => {
              const count = dist[b.key] ?? 0;
              const pct = distTotal > 0 ? (count / distTotal) * 100 : 0;
              if (count === 0) return null;
              return (
                <div key={b.key} className="frs-dist-segment" style={{ width: `${pct}%`, background: b.color }}
                  title={`${b.label}: ${count} document${count !== 1 ? 's' : ''}`} />
              );
            })}
          </div>
          <div className="frs-dist-legend">
            {distBuckets.map(b => {
              const count = dist[b.key] ?? 0;
              return (
                <div key={b.key} className="frs-dist-legend-item">
                  <span className="frs-dist-swatch" style={{ background: b.color }} />
                  <span style={{ color: '#374151' }}>{b.label}</span>
                  <strong style={{ color: '#111827', marginLeft: 4 }}>{count}</strong>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {summary && (
        <div className="frs-section">
          <div className="frs-section-title">Executive Summary</div>
          <div className="frs-prose">{summary}</div>
        </div>
      )}

      {(findings.length > 0 || gaps.length > 0) && (
        <div className="frs-two-col">
          {findings.length > 0 && (
            <div className="frs-section frs-section-col">
              <div className="frs-section-title">Key Findings <span className="frs-count">({findings.length})</span></div>
              <ol className="frs-ordered-list">
                {findings.map((f, i) => <li key={i}>{f}</li>)}
              </ol>
            </div>
          )}
          {gaps.length > 0 && (
            <div className="frs-section frs-section-col">
              <div className="frs-section-title">Gaps Identified <span className="frs-count">({gaps.length})</span></div>
              <ol className="frs-ordered-list">
                {gaps.map((g, i) => <li key={i}>{g}</li>)}
              </ol>
            </div>
          )}
        </div>
      )}

      {details.length > 0 && (
        <div className="frs-section">
          <div className="frs-section-title frs-section-title-row">
            <span>Documents <span className="frs-count">({details.length})</span></span>
            <select className="frs-sort-select" value={docSortBy} onChange={e => setDocSortBy(e.target.value as 'relevance' | 'title')}>
              <option value="relevance">Sort by relevance</option>
              <option value="title">Sort by title</option>
            </select>
          </div>
          <div className="frs-doc-table-wrap">
            <table className="frs-doc-table">
              <thead>
                <tr>
                  <th className="frs-th frs-th-num">#</th>
                  <th className="frs-th frs-th-title">Document</th>
                  <th className="frs-th frs-th-relevance">Relevance</th>
                  <th className="frs-th frs-th-enriched">Enriched</th>
                  <th className="frs-th frs-th-rationale">Rationale</th>
                </tr>
              </thead>
              <tbody>
                {sortedDocs.map((d, i) => {
                  const pct = (d['relevance_percent'] as number) ?? 0;
                  const badge = relevanceBadge(pct);
                  return (
                    <tr key={i} className={i % 2 === 0 ? 'frs-row-even' : 'frs-row-odd'}>
                      <td className="frs-td frs-td-num">{i + 1}</td>
                      <td className="frs-td frs-td-title">{String(d['label'] ?? d['document_id'] ?? '')}</td>
                      <td className="frs-td frs-td-relevance">
                        <div className="frs-rel-cell">
                          <div className="frs-rel-bar-bg">
                            <div className="frs-rel-bar-fill" style={{ width: `${pct}%`, background: badge.bar }} />
                          </div>
                          <span className="frs-rel-badge" style={{ background: badge.bg, color: badge.text }}>{pct}%</span>
                        </div>
                      </td>
                      <td className="frs-td frs-td-enriched">{d['enriched'] ? 'Yes' : '—'}</td>
                      <td className="frs-td frs-td-rationale">{String(d['rationale'] ?? '')}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Legacy inline result (fallback) */}
      {!details.length && result['total_documents'] !== undefined && (
        <FullKBReviewFinalResult result={result} />
      )}
    </div>
  );
}
